import React from 'react';
import { useSelector } from 'react-redux';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import SenderCard from './SenderCard';

export default function SendersPanel({
  senders,
  errors,
  onUpdate,
  onRemove,
  onOpenAddModal,
  routingStrategy,
  roundRobinPrimaries,
  onSetPrimary,
}) {
  // Connectors are in the global store; use them to group senders by ConnectorType
  const connectors = useSelector(s => s.globalData.connectors) || [];

  const getSenderConnectorType = (sender) => {
    if (!sender?.vendorAccountId || !connectors.length) return 'Unknown';
    const connector = connectors.find(c => c.id === sender.vendorAccountId);
    return connector?.connectorType || 'Unknown';
  };

  // Group senders while preserving their original flat index (used by useSenders operations)
  const groupedSenders = senders.reduce((acc, sender, index) => {
    const type = getSenderConnectorType(sender);
    if (!acc[type]) acc[type] = [];
    acc[type].push({ sender, index });
    return acc;
  }, {});

  // ROUND_ROBIN = per-type primary selection (radio buttons shown)
  // ROUND_ROBIN_BY_NUMBER = even split (no radio buttons; active checkbox drives it)
  const isRoundRobin = routingStrategy === 'ROUND_ROBIN';

  return (
    <div className="mt-6 bg-white dark:bg-black rounded-xl border border-gray-200 dark:border-gray-800">
      {/* Panel header */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-800 flex flex-wrap items-center justify-between gap-2">
        <p className="text-left text-md font-semibold text-gray-900 dark:text-gray-100">Senders</p>
        {isRoundRobin && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Click <span className="font-semibold">●</span> to choose the primary sender per
            connector type. It receives that type's equal share of traffic; all others get 0%.
          </p>
        )}
        {routingStrategy === 'ROUND_ROBIN_BY_NUMBER' && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Percentages are split evenly across all active senders. Toggle Active to include or
            exclude a sender.
          </p>
        )}
      </div>

      <div className="space-y-6 p-6">
        {Object.entries(groupedSenders).map(([connType, items]) => (
          <div key={connType}>
            {/* ConnectorType group header (omit for Unknown) */}
            {connType !== 'Unknown' && (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                  {connType}
                </span>
                <span className="flex-1 border-t border-indigo-200 dark:border-indigo-800" />
              </div>
            )}

            <div className="space-y-6">
              {items.map(({ sender, index }) => (
                <SenderCard
                  key={sender.id}
                  sender={sender}
                  index={index}
                  connectorType={connType !== 'Unknown' ? connType : null}
                  isPrimary={isRoundRobin && roundRobinPrimaries?.[connType] === sender.id}
                  onSetPrimary={isRoundRobin && onSetPrimary
                    ? () => onSetPrimary(connType, sender.id)
                    : undefined}
                  routingStrategy={routingStrategy}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  errors={errors}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Add sender */}
        <div
          className="w-full p-4 rounded-lg cursor-pointer border-2 border-dashed border-gray-300 dark:border-gray-700 hover:border-gray-400 hover:dark:border-gray-600 text-center text-black dark:text-white"
          onClick={onOpenAddModal}
        >
          + Add a sender
        </div>

        {errors?.message && (
          <div className="rounded-md bg-red-50 p-4 dark:bg-red-950 mb-6">
            <div className="flex">
              <div className="shrink-0">
                <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-red-400 dark:text-red-500" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">{errors.message}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
